import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const FilterPanel = ({ filters, onFilterChange, onClearFilters, rfqCounts }) => {
  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'pending', label: 'Pending Review' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'quoted', label: 'Quote Sent' },
    { value: 'rejected', label: 'Rejected' }
  ];

  const priorityOptions = [
    { value: '', label: 'All Priorities' },
    { value: 'high', label: 'High Priority' },
    { value: 'medium', label: 'Medium Priority' },
    { value: 'low', label: 'Low Priority' }
  ];

  const categoryOptions = [
    { value: '', label: 'All Categories' },
    { value: 'consultancy', label: 'Consultancy' },
    { value: 'infrastructure', label: 'Infrastructure' },
    { value: 'networking', label: 'Networking' },
    { value: 'software', label: 'Software Development' },
    { value: 'policy', label: 'Policy & Strategy' },
    { value: 'integration', label: 'System Integration' }
  ];

  const assigneeOptions = [
    { value: '', label: 'All Assignees' },
    { value: 'unassigned', label: 'Unassigned' },
    { value: 'john_doe', label: 'John Doe' },
    { value: 'jane_smith', label: 'Jane Smith' },
    { value: 'mike_wilson', label: 'Mike Wilson' },
    { value: 'sarah_johnson', label: 'Sarah Johnson' }
  ];

  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'priority', label: 'Priority' },
    { value: 'deadline', label: 'Deadline' },
    { value: 'value', label: 'Estimated Value' }
  ];

  const handleFilterChange = (key, value) => {
    onFilterChange({ ...filters, [key]: value });
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters)?.filter(value => value && value !== '')?.length;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-foreground flex items-center space-x-2">
          <Icon name="Filter" size={18} />
          <span>Filters</span>
          {getActiveFiltersCount() > 0 && (
            <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium bg-primary text-primary-foreground rounded-full">
              {getActiveFiltersCount()}
            </span>
          )}
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearFilters}
          disabled={getActiveFiltersCount() === 0}
        >
          Clear All
        </Button>
      </div>
      {/* Search */}
      <div>
        <Input
          type="search"
          placeholder="Search RFQs, customers, or companies..."
          value={filters?.search || ''}
          onChange={(e) => handleFilterChange('search', e?.target?.value)}
          className="w-full"
        />
      </div>
      {/* Quick Status Filters */}
      <div>
        <p className="text-sm font-medium text-foreground mb-2">Quick Status</p>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => handleFilterChange('status', 'pending')}
            className={`flex items-center justify-between p-2 rounded-lg border transition-colors ${
              filters?.status === 'pending' ?'border-warning bg-warning/10 text-warning' :'border-border hover:bg-muted'
            }`}
          >
            <span className="text-sm font-medium">Pending</span>
            <span className="text-xs bg-warning/20 text-warning px-2 py-1 rounded-full">
              {rfqCounts?.pending || 0}
            </span>
          </button>
          <button
            onClick={() => handleFilterChange('status', 'in_progress')}
            className={`flex items-center justify-between p-2 rounded-lg border transition-colors ${
              filters?.status === 'in_progress' ?'border-primary bg-primary/10 text-primary' :'border-border hover:bg-muted'
            }`}
          >
            <span className="text-sm font-medium">In Progress</span>
            <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded-full">
              {rfqCounts?.in_progress || 0}
            </span>
          </button>
          <button
            onClick={() => handleFilterChange('status', 'quoted')}
            className={`flex items-center justify-between p-2 rounded-lg border transition-colors ${
              filters?.status === 'quoted' ?'border-success bg-success/10 text-success' :'border-border hover:bg-muted'
            }`}
          >
            <span className="text-sm font-medium">Quoted</span>
            <span className="text-xs bg-success/20 text-success px-2 py-1 rounded-full">
              {rfqCounts?.quoted || 0}
            </span>
          </button>
          <button
            onClick={() => handleFilterChange('status', 'rejected')}
            className={`flex items-center justify-between p-2 rounded-lg border transition-colors ${
              filters?.status === 'rejected' ?'border-error bg-error/10 text-error' :'border-border hover:bg-muted'
            }`}
          >
            <span className="text-sm font-medium">Rejected</span>
            <span className="text-xs bg-error/20 text-error px-2 py-1 rounded-full">
              {rfqCounts?.rejected || 0}
            </span>
          </button>
        </div>
      </div>
      {/* Detailed Filters */}
      <div className="space-y-3">
        <Select
          label="Priority"
          options={priorityOptions}
          value={filters?.priority || ''}
          onChange={(value) => handleFilterChange('priority', value)}
        />

        <Select
          label="Category"
          options={categoryOptions}
          value={filters?.category || ''}
          onChange={(value) => handleFilterChange('category', value)}
        />

        <Select
          label="Assigned To"
          options={assigneeOptions}
          value={filters?.assignee || ''}
          onChange={(value) => handleFilterChange('assignee', value)}
        />

        <Select
          label="Sort By"
          options={sortOptions}
          value={filters?.sortBy || 'newest'}
          onChange={(value) => handleFilterChange('sortBy', value)}
        />
      </div>
      {/* Date Range */}
      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">Date Range</p>
        <div className="grid grid-cols-2 gap-2">
          <Input
            type="date"
            label="From"
            value={filters?.dateFrom || ''}
            onChange={(e) => handleFilterChange('dateFrom', e?.target?.value)}
          />
          <Input
            type="date"
            label="To"
            value={filters?.dateTo || ''}
            onChange={(e) => handleFilterChange('dateTo', e?.target?.value)}
          />
        </div>
      </div>
      {/* Value Range */}
      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">Estimated Value (UGX)</p>
        <div className="grid grid-cols-2 gap-2">
          <Input
            type="number"
            label="Min Value"
            placeholder="0"
            value={filters?.minValue || ''}
            onChange={(e) => handleFilterChange('minValue', e?.target?.value)}
          />
          <Input
            type="number"
            label="Max Value"
            placeholder="No limit"
            value={filters?.maxValue || ''}
            onChange={(e) => handleFilterChange('maxValue', e?.target?.value)}
          />
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;